import React, { useState, useEffect } from 'react';
import { Home, Wallet, UserRound } from 'lucide-react';
import { AppProvider, useAppContext } from './context/AppContext';
import Navbar from './components/Navbar';
import LandingPage from './components/LandingPage';
import HomeView from './components/HomeView';
import SettingsView from './components/SettingsView';
import ActivityView from './components/ActivityView';
import RealityCheckView from './components/RealityCheckView';
import LeakTargetsView from './components/LeakTargetsView';
import UpiShareCard from './components/UpiShareCard';
import PasswordModal from './components/PasswordModal';
import ManualTransactionFAB from './components/ManualTransactionFAB';
import ManualTransactionModal from './components/ManualTransactionModal';
import PwaChrome from './components/PwaChrome';
import './App.css';

const TABS = [
  { key: 'home', label: 'Home', icon: Home },
  { key: 'activity', label: 'Spend', icon: Wallet },
  { key: 'settings', label: 'Profile', icon: UserRound },
];

function resolveScreen({ user, allTransactions, appScreen, isSampleMode }) {
  if (!user) return 'landing';
  if (appScreen === 'reality' || appScreen === 'targets' || appScreen === 'app') {
    if (appScreen !== 'auto') return appScreen;
  }
  if (isSampleMode) return 'app';
  if (!allTransactions.length) return 'landing-guest'; // guest with no data — show dropzone via landing handlers
  const realityDone = localStorage.getItem('spenderly_reality_done') === '1';
  const targetsSet = localStorage.getItem('spenderly_leak_targets_set') === '1';
  if (!realityDone) return 'reality';
  if (!targetsSet) return 'targets';
  return 'app';
}

function AppContent() {
  const ctx = useAppContext();
  const {
    activeTab,
    setActiveTab,
    allTransactions,
    user,
    handleLogin,
    loadSampleData,
    isSampleMode,
    showSampleLocked,
    setShowSampleLocked,
    clearSampleData,
    handleFile,
    passwordRequest,
    setPasswordRequest,
    status,
    setStatus,
    appScreen,
    setAppScreen,
  } = ctx;

  const [shareOpen, setShareOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    if (!status?.active || status.type === 'loading') return undefined;
    const t = setTimeout(() => {
      setStatus({ active: false, text: '', type: '' });
    }, 4200);
    return () => clearTimeout(t);
  }, [status, setStatus]);

  const screen = resolveScreen({ user, allTransactions, appScreen, isSampleMode });

  const onLandingFile = async (source, file) => {
    handleLogin({ id: 'guest-user', name: 'Guest', email: '', plan: 'free' });
    try {
      await handleFile(source, file, false);
    } catch { /* status in context */ }
  };

  const enterDemoMode = () => {
    handleLogin({ id: 'demo-user', name: 'Demo User', email: 'demo@spenderly.ai', plan: 'free' });
    setTimeout(() => loadSampleData(), 100);
  };

  const switchToOwnData = () => {
    clearSampleData();
    handleLogin({ id: 'guest-user', name: 'Guest', email: '', plan: 'free' });
    setShowSampleLocked(false);
    setAppScreen('auto');
  };

  // Landing (no session or guest with empty DB wanting dropzone)
  if (screen === 'landing' || (screen === 'landing-guest' && !user)) {
    return (
      <>
        <LandingPage
          onFileDrop={onLandingFile}
          onTrySample={enterDemoMode}
          passwordRequest={passwordRequest}
          setPasswordRequest={setPasswordRequest}
          status={status}
        />
        {status?.active && status.type !== 'loading' && (
          <div className={`sp-toast status ${status.type}`} role="status">
            {status.text}
          </div>
        )}
      </>
    );
  }

  // Guest logged in but no txns yet — still show landing dropzone
  if (screen === 'landing-guest') {
    return (
      <>
        <LandingPage
          onFileDrop={onLandingFile}
          onTrySample={enterDemoMode}
          passwordRequest={passwordRequest}
          setPasswordRequest={setPasswordRequest}
          status={status}
        />
        {status?.active && status.type !== 'loading' && (
          <div className={`sp-toast status ${status.type}`} role="status">
            {status.text}
          </div>
        )}
      </>
    );
  }

  if (screen === 'reality') {
    return (
      <div className="flow-shell">
        <div className="flow-shell-inner">
          <RealityCheckView
            onContinue={() => setAppScreen('targets')}
            onShare={() => setShareOpen(true)}
          />
        </div>
        <UpiShareCard open={shareOpen} onClose={() => setShareOpen(false)} />
        {passwordRequest && (
          <PasswordModal
            title="Statement is locked"
            message="Enter password (processed strictly on device)."
            onSubmit={(pwd) => { passwordRequest.callback(pwd); setPasswordRequest(null); }}
            onCancel={() => { passwordRequest.callback(new Error('Password cancelled')); setPasswordRequest(null); }}
          />
        )}
      </div>
    );
  }

  if (screen === 'targets') {
    return (
      <div className="flow-shell">
        <div className="flow-shell-inner">
          <LeakTargetsView onContinue={() => { setAppScreen('app'); setActiveTab('home'); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {status?.active && status.type !== 'loading' && (
        <div className={`sp-toast status ${status.type}`} role="status">
          {status.text}
        </div>
      )}
      <nav className="app-sidebar">
        <div className="sidebar-logo">
          <span className="logo-text">spenderly</span>
        </div>
        <div className="sidebar-menu">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`sidebar-item ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon className="sidebar-icon" size={20} />
                <span className="sidebar-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <main className="app-main">
        <Navbar />
        <div className="view-container">
          {activeTab === 'home' && <HomeView onShare={() => setShareOpen(true)} />}
          {activeTab === 'activity' && <ActivityView />}
          {activeTab === 'settings' && <SettingsView />}
        </div>

        {showSampleLocked && (
          <div
            className="sp-modal-overlay"
            role="presentation"
            onClick={() => setShowSampleLocked(false)}
          >
            <div
              className="sp-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sample-locked-title"
              onClick={e => e.stopPropagation()}
            >
              <h3 id="sample-locked-title" style={{ margin: '0 0 8px', fontSize: 20 }}>You&apos;re viewing sample data</h3>
              <p className="sp-modal-message" style={{ marginBottom: 20 }}>
                Switch to your own statements to edit. Uploads stay on this device.
              </p>
              <div className="sp-modal-actions">
                <button type="button" className="sp-btn-ghost" onClick={() => setShowSampleLocked(false)}>
                  Continue exploring demo
                </button>
                <button type="button" className="sp-btn-primary" onClick={switchToOwnData}>
                  Use my own UPI data →
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <nav className="mobile-bottom-nav">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={`mob-${tab.key}`}
              className={`bottom-nav-item ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={20} className="bottom-nav-icon" />
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <UpiShareCard open={shareOpen} onClose={() => setShareOpen(false)} />
      <ManualTransactionFAB onClick={() => setManualOpen(true)} />
      <ManualTransactionModal isOpen={manualOpen} onClose={() => setManualOpen(false)} />
      <PwaChrome variant="app" />
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
